import React from 'react';
import { useVisibilityChange } from '../../CustomHooks/UseVisibilityChange';

import { ActionType, CartContextInterface, CartItemType, LocalCart as LocalCartType } from '../Cart/Types';

const readCartFromLocalStorage = (): LocalCartType | null => {
    const savedCart = localStorage.getItem('cart');

    if (typeof savedCart === 'string' && JSON.parse(savedCart).hasOwnProperty('items')) {
        return JSON.parse(savedCart);
    }

    return null;
};

const emptyCart: LocalCartType = {
    mode: 'local',
    items: [],
    visible: false,
};

const deleteCart = () => {
    localStorage.removeItem('cart');
};

const createEmptyCart = () => {
    deleteCart();
    localStorage.setItem('cart', JSON.stringify(emptyCart));
};

const saveCartToLocalStorage = (cart: LocalCartType) => {
    localStorage.setItem('cart', JSON.stringify(cart));
};

const initialCart = (): LocalCartType => {
    const cartFromLocalStorage = readCartFromLocalStorage();

    if (cartFromLocalStorage && cartFromLocalStorage.items.length > 0) {
        return cartFromLocalStorage;
    } else {
        // no former (filled) cart present in local storage,
        // create empty cart and read again
        createEmptyCart();
        const emptyCartFromLocalStorage = readCartFromLocalStorage();
        if (emptyCartFromLocalStorage) {
            return emptyCartFromLocalStorage;
        }
    }

    throw new Error('Could not create cart in local storage');
};

// we need this to determine if we
// are currently waiting in a timeout
// for the ``latestAction`` state to be
// cleared (for notification to vanish)
let cleartingLatestAction = false;

export const LocalCartContext = React.createContext<CartContextInterface>({
    cart: initialCart(),
    updateCartItems: (item, action) => {},
    setCartVisibility: () => {},
    activeItems: [],
    deleteCart,
    latestAction: null,
    syncFailure: false,
    totalItemsInCarts: 0,
    refreshCart: (visibilityOverride?: boolean) => {},
});

export const LocalCartContextValueProvider: React.FunctionComponent = (props) => {
    const [cart, updateCart] = React.useState<LocalCartType>(initialCart());
    const [latestAction, setLatestAction] = React.useState<ActionType | null>(null);

    useVisibilityChange(() => updateCart(initialCart()));

    const refreshCart = (visibilityOverride?: boolean) => {
        const cartFromLocalStorage = readCartFromLocalStorage() ?? initialCart();
        updateCart({ ...cartFromLocalStorage, visible: visibilityOverride ?? cartFromLocalStorage.visible });
    };

    const updateCartItems = (updateItem: CartItemType, action: ActionType) => {
        // apply action on cart form local storage to stay consistent accross tabs without refresh
        const newCart = initialCart();

        // reset latest action after 5 second timeout
        const resetLatestAction = () => {
            if (cleartingLatestAction === false) {
                setTimeout(() => {
                    setLatestAction(null);
                    cleartingLatestAction = false;
                }, 5000);
            }
            cleartingLatestAction = true;
        };

        switch (action) {
            case ActionType.Add:
                // remove possible duplicates because on an runtime error
                newCart.items = newCart.items.filter(
                    (cartItem: CartItemType) => cartItem.articleNumber !== updateItem.articleNumber,
                );

                if (updateItem.quantity !== 0) {
                    newCart.items.push(updateItem);
                }
                setLatestAction(ActionType.Add);

                break;

            case ActionType.Remove:
                newCart.items = newCart.items.filter(
                    (cartItem: CartItemType) => cartItem.articleNumber !== updateItem.articleNumber,
                );
                break;

            case ActionType.Update:
                // if quantity is 0, remove element
                newCart.items = newCart.items.filter((cartItem: CartItemType) => {
                    if (cartItem.articleNumber === updateItem.articleNumber) {
                        return updateItem.quantity !== 0;
                    }

                    return true;
                });
                // if element was found in cart, update
                newCart.items = newCart.items.map((cartItem: CartItemType) => {
                    if (cartItem.articleNumber === updateItem.articleNumber) {
                        return updateItem;
                    } else {
                        return cartItem;
                    }
                });

                setLatestAction(ActionType.Update);
                break;
        }

        newCart.visible = true;
        resetLatestAction();
        saveCartToLocalStorage(newCart);
        updateCart(newCart);
    };

    const setCartVisibility = (visibility: boolean) => {
        const newCart = { ...cart };
        newCart.visible = visibility;
        updateCart(newCart);
        saveCartToLocalStorage(newCart);
    };

    return (
        <LocalCartContext.Provider
            value={{
                cart,
                updateCartItems,
                setCartVisibility,
                activeItems: cart.items,
                deleteCart,
                latestAction,
                syncFailure: false,
                totalItemsInCarts: cart.items.length,
                refreshCart,
            }}
        >
            {props.children}
        </LocalCartContext.Provider>
    );
};
